/**
 * Utility functions for exporting data to CSV
 */
import Papa from 'papaparse';
import { getFieldZone } from '../constants/rugbyConstants';

/**
 * Format event data for CSV export
 * 
 * @param {Array} events - Event data to export
 * @param {string} homeTeamName - Name of home team
 * @param {string} awayTeamName - Name of away team
 * @returns {Array} Formatted data ready for CSV conversion
 */
export const formatEventsForCsv = (
  events, 
  homeTeamName, 
  awayTeamName
) => {
  return events.map((event, index) => ({
    'Event #': index + 1,
    'Time': event.timestamp,
    'Period': event.period || '',
    'Event Type': event.eventType,
    'Team': event.team === 'Home' ? homeTeamName : awayTeamName,
    'Zone': event.zone || getFieldZone(event.position.x, event.period), // Add zone information with period consideration
    'Comment': event.comment || '',
    'X Position': event.position.x.toFixed(3),
    'Y Position': event.position.y.toFixed(3)
  }));
};

/**
 * Export events to CSV file
 * 
 * @param {Array} events - Event data to export
 * @param {string} homeTeamName - Name of home team
 * @param {string} awayTeamName - Name of away team
 * @param {Function} showToast - Optional function to display a toast notification
 * @returns {Promise<void>}
 */
export const exportEventsToCsv = (
  events,
  homeTeamName,
  awayTeamName,
  showToast = null
) => {
  if (events.length === 0) {
    if (showToast) {
      showToast('No events to export', 'error');
    } else {
      alert('No events to export');
    }
    return;
  }

  const csvData = formatEventsForCsv(
    events, 
    homeTeamName, 
    awayTeamName
  );

  const csv = Papa.unparse(csvData);
  const fileName = `${homeTeamName.replace(/\s+/g, '_')}_vs_${awayTeamName.replace(/\s+/g, '_')}_events.csv`;
  
  // Web browser approach - Add UTF-8 BOM to ensure proper encoding of special characters (umlauts, etc.)
  const bom = '\uFEFF';
  const blob = new Blob([bom + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  
  link.setAttribute('href', url);
  link.setAttribute('download', fileName);
  link.style.display = 'none';
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  
  if (showToast) {
    showToast('Exported to CSV successfully', 'success');
  }
};